import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainer } from '@/components/calculator/FieldExplainer';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { Select } from '@/components/ui/select';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getCoastFireFieldExplainers } from '@/content/field-explainers/coast-fire';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateCoastFire, type CoastInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  formatPercent,
  parseContractState
} from './shared';

type CoastState = {
  currentAge: number;
  retirementAge: number;
  currentSavings: number;
  targetMethod: 'expenses' | 'number';
  annualExpenses: number;
  withdrawalRate: number;
  targetFIRENumber: number;
  expectedReturn: number;
  inflationRate: number;
};

const defaults: CoastState = {
  currentAge: 32,
  retirementAge: 58,
  currentSavings: 175000,
  targetMethod: 'expenses',
  annualExpenses: 72000,
  withdrawalRate: 4,
  targetFIRENumber: 1800000,
  expectedReturn: 7,
  inflationRate: 2.5
};

// A hand-edited share URL can carry a zero or negative withdrawal rate. The
// engine falls back to 4% for those, so the visible field, share links, and
// PDF export must land on the same number or the page silently lies about
// which rate produced the results.
function normalizeCoastState(state: CoastState): CoastState {
  return state.withdrawalRate > 0 ? state : { ...state, withdrawalRate: 4 };
}

const chartConfig = {
  projected: {
    label: 'Projected balance',
    color: 'var(--chart-1)'
  },
  target: {
    label: 'FIRE target line',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

type Props = {
  route: RouteDefinition;
};

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

function toEngineInput(state: CoastState): CoastInput {
  return {
    currentAge: state.currentAge,
    retirementAge: state.retirementAge,
    currentSavings: state.currentSavings,
    targetMethod: state.targetMethod,
    annualExpenses: state.annualExpenses,
    withdrawalRate: state.withdrawalRate,
    targetFIRENumber: state.targetFIRENumber,
    expectedReturn: state.expectedReturn,
    inflationRate: state.inflationRate
  };
}

export function CoastFireCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<CoastState>(() => normalizeCoastState(parseContractState('coastFire', defaults)));

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculateCoastFire(toEngineInput(state)), [state]);

  const explainers = useMemo(() => {
    const entries = getCoastFireFieldExplainers({
      input: {
        currentAge: state.currentAge,
        retirementAge: state.retirementAge,
        currentSavings: state.currentSavings,
        targetMethod: state.targetMethod,
        annualExpenses: state.annualExpenses,
        withdrawalRate: state.withdrawalRate,
        targetFIRENumber: state.targetFIRENumber,
        expectedReturn: state.expectedReturn,
        inflationRate: state.inflationRate
      },
      result: {
        yearsToRetirement: result.yearsToRetirement,
        realReturn: result.realReturn,
        calculatedFIRENumber: result.calculatedFIRENumber,
        coastFIRENumber: result.coastFIRENumber,
        projectedBalance: result.projectedBalance,
        isCoastFIRE: result.isCoastFIRE,
        monthlySavingsNeeded: result.monthlySavingsNeeded,
        savingsWindowMonths: result.savingsWindowMonths,
        hasSavingsWindow: result.hasSavingsWindow
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result]);

  const chartData = useMemo(
    () =>
      result.chart.labels.map((age, index) => ({
        age,
        projected: result.chart.projectionData[index],
        target: result.chart.fireTargetLine[index]
      })),
    [result.chart.fireTargetLine, result.chart.labels, result.chart.projectionData]
  );

  const shareState = {
    currentAge: state.currentAge,
    retirementAge: state.retirementAge,
    currentSavings: state.currentSavings,
    targetMethod: state.targetMethod,
    annualExpenses: state.annualExpenses,
    withdrawalRate: state.withdrawalRate,
    targetFIRENumber: state.targetFIRENumber,
    expectedReturn: state.expectedReturn,
    inflationRate: state.inflationRate
  };

  return (
    <AppLayout showRouteHowItWorks={false}>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Coast FIRE Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Estimate when your current savings can coast to your retirement target without additional contributions.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel
          title="Coast assumptions"
          description="Set your target method, savings, and return assumptions to test Coast FIRE timing."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="coastCurrentAge" label="Current age" value={state.currentAge} min={18} max={85} parseMode="integer" explainer={explainers.byId.get('currentAge')} onChange={(value) => setState((prev) => ({ ...prev, currentAge: value }))} />
            <NumberField id="coastRetireAge" label="Retirement age" value={state.retirementAge} min={state.currentAge + 1} max={90} parseMode="integer" explainer={explainers.byId.get('retirementAge')} onChange={(value) => setState((prev) => ({ ...prev, retirementAge: value }))} />
            <NumberField id="coastSavings" label="Current savings" value={state.currentSavings} min={0} step={1000} prefix="$" explainer={explainers.byId.get('currentSavings')} onChange={(value) => setState((prev) => ({ ...prev, currentSavings: value }))} />
            <NumberField id="coastExpectedReturn" label="Expected return" value={state.expectedReturn} min={0} max={12} step={0.1} suffix="%" explainer={explainers.byId.get('expectedReturn')} onChange={(value) => setState((prev) => ({ ...prev, expectedReturn: value }))} />
            <NumberField id="coastInflation" label="Inflation rate" value={state.inflationRate} min={0} max={7} step={0.1} suffix="%" explainer={explainers.byId.get('inflationRate')} onChange={(value) => setState((prev) => ({ ...prev, inflationRate: value }))} />
            <NumberField id="coastWithdrawal" label="Withdrawal rate" value={state.withdrawalRate} min={2} max={7} step={0.1} suffix="%" explainer={explainers.byId.get('withdrawalRate')} onChange={(value) => setState((prev) => ({ ...prev, withdrawalRate: value }))} />
          </div>

          <div className="space-y-2 text-sm">
            <span className="flex items-center gap-1.5">
              <label className="text-sm font-semibold text-foreground" htmlFor="coast-target-method">
                Target method
              </label>
              {explainers.byId.get('targetMethod') ? (
                <FieldExplainer explainer={explainers.byId.get('targetMethod')!} />
              ) : null}
            </span>
            <Select
              id="coast-target-method"
              value={state.targetMethod}
              onChange={(event) => setState((prev) => ({ ...prev, targetMethod: event.target.value as 'expenses' | 'number' }))}
              options={[
                { value: 'expenses', label: 'Use annual expenses' },
                { value: 'number', label: 'Use direct FIRE number' }
              ]}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="coastAnnualExpenses" label="Annual expenses" value={state.annualExpenses} min={10000} step={500} prefix="$" explainer={explainers.byId.get('annualExpenses')} onChange={(value) => setState((prev) => ({ ...prev, annualExpenses: value }))} />
            <NumberField id="coastTargetNumber" label="Direct FIRE target" value={state.targetFIRENumber} min={100000} step={5000} prefix="$" explainer={explainers.byId.get('targetFIRENumber')} onChange={(value) => setState((prev) => ({ ...prev, targetFIRENumber: value }))} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Required coast number" value={formatCurrency(result.coastFIRENumber)} note="present-value target at your current age" trend="flat" explainer={explainers.byId.get('coastFIRENumber')} />
          <ResultSummaryCard label="Projected balance" value={formatCurrency(result.projectedBalance)} note={`after ${result.yearsToRetirement} years`} trend={result.isCoastFIRE ? 'up' : 'flat'} explainer={explainers.byId.get('projectedBalance')} />
          <ResultSummaryCard label="Current status" value={result.isCoastFIRE ? 'Already Coast FIRE' : 'Not Yet Coast FIRE'} note={
              result.isCoastFIRE
                ? 'Current savings already exceed coast threshold'
                : result.hasSavingsWindow
                  ? `${formatCurrency(result.monthlySavingsNeeded)} monthly needed for ${result.savingsWindowMonths} months`
                  : 'No saving years left — the full gap is due now'
            } trend={result.isCoastFIRE ? 'up' : 'down'} explainer={explainers.byId.get('currentStatus')} />
          <ResultSummaryCard label="Real return" value={formatPercent(result.realReturn)} note="adjusted for inflation" trend="flat" explainer={explainers.byId.get('realReturn')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'coastFire', shareState);
              const ok = await copyToClipboard(shareUrl);
              toast({
                title: ok ? 'Share link copied' : 'Share link ready',
                description: ok ? shareUrl : `Copy manually: ${shareUrl}`,
                variant: ok ? 'success' : 'warning'
              });
            }}
            onExportCsv={() => {
              const headers = ['Age', 'Projected Balance', 'FIRE Target Line'];
              const rows = chartData.map((row) => [row.age, Math.round(row.projected), Math.round(row.target)]);
              exportRowsToCsv(headers, rows, 'coast-fire-projection.csv');
              toast({ title: 'CSV exported', description: 'coast-fire-projection.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Coast FIRE Projection',
                subtitle: `Age ${state.currentAge} to ${state.retirementAge}`,
                summaryLeft: [
                  `Current savings: ${formatCurrency(state.currentSavings)}`,
                  `Expected return: ${state.expectedReturn.toFixed(1)}%`,
                  `Inflation rate: ${state.inflationRate.toFixed(1)}%`,
                  `Withdrawal rate: ${state.withdrawalRate.toFixed(1)}%`
                ],
                summaryRight: [
                  `Coast number: ${formatCurrency(result.coastFIRENumber)}`,
                  `Projected balance: ${formatCurrency(result.projectedBalance)}`,
                  `Real return: ${formatPercent(result.realReturn)}`,
                  `Status: ${result.isCoastFIRE ? 'Already coast FIRE' : 'Not yet coast FIRE'}`
                ],
                headers: ['Age', 'Projected Balance', 'FIRE Target'],
                rows: chartData.map((row) => [row.age, formatCurrency(row.projected), formatCurrency(row.target)]),
                filename: 'coast-fire-projection.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'coast-fire-projection.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Projected balance vs FIRE target"
          description="Complete year-by-year trajectory relative to your target line."
          annotation={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                Coast number <span className="font-semibold text-foreground">{formatCurrency(result.coastFIRENumber)}</span>
              </span>
              <span>
                Projected balance <span className="font-semibold text-foreground">{formatCurrency(result.projectedBalance)}</span>
              </span>
            </div>
          }
          legend={<ChartLegendPills config={chartConfig} keys={['projected', 'target']} />}
        >
          <ChartContainer config={chartConfig} family="comparison">
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="age" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={70}
                tickFormatter={(value) => formatCompactCurrency(Number(value))}
              />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />}
              />
              <Line dataKey="projected" type="natural" stroke="var(--color-projected)" strokeWidth={3.1} dot={false} />
              <Line dataKey="target" type="natural" stroke="var(--color-target)" strokeWidth={1.9} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Projection table" description="Full age-by-age projected and target balances.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Age</TableHead>
                <TableHead numeric>Projected</TableHead>
                <TableHead numeric>Target</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chartData.map((row) => (
                <TableRow key={row.age}>
                  <TableCell numeric>{row.age}</TableCell>
                  <TableCell numeric>{formatCurrency(row.projected)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.target)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="What every input and result on this page does to the math, with your own figures substituted in. The info icon beside each field opens the same note where you are."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
